import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHome, faCalculator, faArrowLeft, faCheck, faSpinner, faUser } from '@fortawesome/free-solid-svg-icons';
import axios from '../../axios';
import config from '../../config';
import { trackFormSubmission, getSourceDomain, getValuationMarket } from '../../utils/analytics';
import { useLanguage } from '../../i18n/LanguageContext';
import { getMapboxCountry, getMapboxLanguage } from '../../utils/mapboxCountry';
import './ValuationResult.css';
import { BodexPlusOffer } from '../BodexPlus/BodexPlus';

const ValuationResult = () => {
  const { t, language } = useLanguage();
  // Progress tracking states
  const [currentStep, setCurrentStep] = useState(1);
  const [stepStates, setStepStates] = useState({
    1: 'loading', // loading, completed, error
    2: 'pending', // pending, loading, completed, error
    3: 'pending'  // pending, loading, completed, error
  });
  
  // Data states
  const [valuationResult, setValuationResult] = useState(null);
  const [adCreationResult, setAdCreationResult] = useState(null);
  const [emailResult, setEmailResult] = useState(null);
  const [error, setError] = useState('');
  const [isGenerating, setIsGenerating] = useState(false); // Add guard flag
  
  // Prevent double initialization and multiple submissions
  const initializationRef = useRef(false);
  const valuationSubmittedRef = useRef(false);
  
  const history = useHistory();
  const location = useLocation();
  const backend_url = config.backend_url;
  const MAPBOX_TOKEN = config.mapbox_token;
  
  // Get data from location state
  const { 
    valuationId, 
    address, 
    questionsAnswers, 
    phone: phoneFromChat,
    email: emailFromChat, 
    allAnswers, 
    isPending,
    finalQuestion,
    finalAnswer,
    finalQuestionCount
  } = location.state || {};

  // Qualified ownership state (align with backend):
  // Only "Ja" should trigger "create user + sellerdata + lead" workflow.
  const userOwnsProperty = allAnswers?.ownership === 'Ja';

  // Step definitions for progress tracking - conditional based on ownership
  const steps = userOwnsProperty ? [
    {
      id: 1,
      title: t('valuationResult.generating'),
      description: t('valuationResult.analyzing')
    },
    {
      id: 2,
      title: t('valuationResult.sendingEmail'),
      description: t('valuationResult.emailDescription')
    }
  ] : [
    {
      id: 1,
      title: t('valuationResult.generating'),
      description: t('valuationResult.analyzing')
    },
    {
      id: 2,
      title: t('valuationResult.sendingEmail'),
      description: t('valuationResult.emailDescription')
    }
  ];

  // Helper functions for localStorage tracking
  const getValuationStorageKey = (valuationId) => `valuation_completed_${valuationId}`;
  const getAdCreationStorageKey = (valuationId) => `ad_created_${valuationId}`;
  const getEmailSentStorageKey = (valuationId) => `email_sent_${valuationId}`;
  const getTimestampKey = (valuationId) => `valuation_timestamp_${valuationId}`;
  
  const isValuationCompleted = (valuationId) => {
    if (!valuationId) return false;
    return localStorage.getItem(getValuationStorageKey(valuationId)) === 'true';
  };
  
  const isAdCreated = (valuationId) => {
    if (!valuationId) return false;
    return localStorage.getItem(getAdCreationStorageKey(valuationId)) === 'true';
  };
  
  const isEmailSent = (valuationId) => {
    if (!valuationId) return false;
    return localStorage.getItem(getEmailSentStorageKey(valuationId)) === 'true';
  };
  
  const markValuationCompleted = (valuationId) => {
    if (valuationId) {
      localStorage.setItem(getValuationStorageKey(valuationId), 'true');
      localStorage.setItem(getTimestampKey(valuationId), Date.now().toString());
    }
  };
  
  const markAdCreated = (valuationId) => {
    if (valuationId) {
      localStorage.setItem(getAdCreationStorageKey(valuationId), 'true');
    }
  };
  
  const markEmailSent = (valuationId) => {
    if (valuationId) {
      localStorage.setItem(getEmailSentStorageKey(valuationId), 'true');
    }
  };
  
  // Cleanup old localStorage entries (older than 7 days)
  const cleanupOldEntries = () => {
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    // Get all localStorage keys
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('valuation_completed_') || key.startsWith('ad_created_') || key.startsWith('email_sent_') || key.startsWith('valuation_timestamp_'))) {
        // Extract valuation ID from the key
        const valuationId = key.replace(/^(valuation_completed_|ad_created_|email_sent_|valuation_timestamp_)/, '');
        const timestampKey = getTimestampKey(valuationId);
        const timestamp = localStorage.getItem(timestampKey);
        
        if (timestamp && parseInt(timestamp) < sevenDaysAgo) {
          keysToRemove.push(getValuationStorageKey(valuationId));
          keysToRemove.push(getAdCreationStorageKey(valuationId));
          keysToRemove.push(getEmailSentStorageKey(valuationId));
          keysToRemove.push(timestampKey);
        }
      }
    }
    
    // Remove old entries
    keysToRemove.forEach(key => localStorage.removeItem(key));
  };

  // Geocode address to get coordinates and län
  const geocodeAddress = async (addressToGeocode) => {
    try {
      if (!addressToGeocode || !MAPBOX_TOKEN) {
        console.warn('Address or Mapbox token missing for geocoding');
        return { latitude: null, longitude: null, lan: '' };
      }

      // Step 1: Forward geocoding to get coordinates
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(
          addressToGeocode
        )}.json?access_token=${MAPBOX_TOKEN}&limit=1&country=${getMapboxCountry(language)}&language=${getMapboxLanguage(language)}`
      );
      
      if (!response.ok) {
        throw new Error(`Geocoding API error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const feature = data.features[0];
        // Mapbox returns [lng, lat]
        const longitude = feature.center[0];
        const latitude = feature.center[1];
        
        // Extract län from forward geocoding context first
        let extractedLan = '';
        if (feature.context) {
          // Look for region context
          for (const context of feature.context) {
            if (context.id && context.id.startsWith('region')) {
              extractedLan = context.text || '';
              // Remove "län" suffix if present and standardize (same as populate_lan_field.py)
              extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
              break;
            }
          }
        }
        
        // Also check if the feature itself is a region
        if (!extractedLan && feature.place_type && feature.place_type.includes('region')) {
          extractedLan = feature.text || '';
          extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
        }
        
        // Step 2: If no län found, try reverse geocoding with coordinates (same as Python script)
        if (!extractedLan) {
          try {
            const reverseResponse = await fetch(
              `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAPBOX_TOKEN}&country=${getMapboxCountry(language)}&types=region&language=${getMapboxLanguage(language)}`
            );
            
            if (reverseResponse.ok) {
              const reverseData = await reverseResponse.json();
              if (reverseData.features && reverseData.features.length > 0) {
                for (const reverseFeature of reverseData.features) {
                  if (reverseFeature.place_type && reverseFeature.place_type.includes('region')) {
                    extractedLan = reverseFeature.text || '';
                    extractedLan = extractedLan.replace(' län', '').replace(' Län', '').trim();
                    break;
                  }
                }
              }
            }
          } catch (reverseError) {
            console.warn('Reverse geocoding failed:', reverseError);
          }
        }
        
        console.log(`Geocoded address "${addressToGeocode}" to:`, { latitude, longitude, lan: extractedLan });
        return { latitude, longitude, lan: extractedLan };
      } else {
        console.warn('No geocoding results found for address:', addressToGeocode);
        return { latitude: null, longitude: null, lan: '' };
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      return { latitude: null, longitude: null, lan: '' };
    }
  };

  // Submit final valuation answer and get result
  const generateValuation = async () => {
    try {
      // Prevent multiple submissions with comprehensive checks
      if (valuationSubmittedRef.current) {
        console.log('Valuation already submitted via ref, aborting');
        return;
      }
      
      // Double-check to prevent duplicate processing
      if (isValuationCompleted(valuationId)) {
        console.log('Valuation already completed during generation attempt, aborting');
        setIsGenerating(false);
        return;
      }
      
      // Mark as submitted immediately to prevent race conditions
      valuationSubmittedRef.current = true;
      
      // If valuation was already submitted from chat (isPending: false), 
      // try to get the result instead of submitting again
      if (!isPending) {
        console.log('Valuation already submitted from chat, checking for result');
        try {
          const getResultResponse = await axios.get(`${backend_url}/api/get-valuation-result/${valuationId}`);
          if (getResultResponse.data.success && getResultResponse.data.valuation_result) {
            console.log('Retrieved existing valuation result');
            setValuationResult(getResultResponse.data.valuation_result);
            setStepStates(prev => ({ ...prev, 1: 'completed' }));
            markValuationCompleted(valuationId);
            
            // Track valuation completion
            trackFormSubmission('valuation_completed', {
              address: address?.replace(/\s+\d+.*$/, ''), // anonymized
              questions_count: questionsAnswers.length,
              event_category: 'Valuation',
              event_label: 'Retrieved Property Valuation Result'
            });
            
            // Continue to ad creation
            if (!isAdCreated(valuationId)) {
              setTimeout(() => {
                setCurrentStep(2);
                setStepStates(prev => ({ ...prev, 2: 'loading' }));
                createUserAndAd(getResultResponse.data.valuation_result);
              }, 1500);
            } else {
              setCurrentStep(2);
              setStepStates(prev => ({ ...prev, 2: 'completed' }));
              setAdCreationResult({ success: true, message: 'Ad already created' });
            }
            return;
          }
        } catch (getError) {
          console.log('Could not retrieve existing result:', getError.response?.status, getError.response?.data);
          
          // If the valuation doesn't exist on the backend, this is a real error
          if (getError.response?.status === 404) {
            setStepStates(prev => ({ ...prev, 1: 'error' }));
            setError(t('valuationResult.noValuation'));
            setIsGenerating(false);
            valuationSubmittedRef.current = false; // Reset on error
            initializationRef.current = false; // Reset on error
            return;
          }
          
          // For other errors, try submitting again only if we have the required data
          if (!questionsAnswers || !allAnswers) {
            setStepStates(prev => ({ ...prev, 1: 'error' }));
            setError(t('valuationResult.missingData'));
            setIsGenerating(false);
            valuationSubmittedRef.current = false; // Reset on error
            initializationRef.current = false; // Reset on error
            return;
          }
          
          console.log('Will try submitting again with available data');
          // If getting result fails, fall through to submitting again
        }
      }
      
      // Use final question data if available, otherwise use last question from chat
      const questionToSubmit = finalQuestion || questionsAnswers[questionsAnswers.length - 1]?.question;
      const answerToSubmit = finalAnswer || questionsAnswers[questionsAnswers.length - 1]?.answer;
      const questionCount = finalQuestionCount || questionsAnswers.length;
      
      // Validate that we have the required data
      if (!questionToSubmit || !answerToSubmit || !allAnswers) {
          setStepStates(prev => ({ ...prev, 1: 'error' }));
          setError(t('valuationResult.missingData'));
          setIsGenerating(false);
          valuationSubmittedRef.current = false; // Reset on error
          initializationRef.current = false; // Reset on error
          return;
        }
      
      console.log('Submitting final valuation answer for ID:', valuationId);
      const sourceDomain = allAnswers?.source_domain || getSourceDomain();
      const valuationMarket = allAnswers?.valuation_market || getValuationMarket();
      const enrichedAnswers = {
        ...allAnswers,
        language,
        source_domain: sourceDomain,
        valuation_market: valuationMarket
      };
      
      const response = await axios.post(`${backend_url}/api/submit-valuation-answer`, {
        valuation_id: valuationId,
        question: questionToSubmit,
        answer: answerToSubmit,
        questions_count: questionCount,
        is_final_question: true,
        all_answers: enrichedAnswers,
        address: address,
        language,
        source_domain: sourceDomain,
        valuation_market: valuationMarket,
        skip_auto_processing: true
      });
      
      if (response.data.success && response.data.valuation_complete) {
        console.log('Valuation completed successfully for ID:', valuationId);
        setValuationResult(response.data.valuation_result);
        setStepStates(prev => ({ ...prev, 1: 'completed' }));
        
        // Mark valuation as completed in localStorage to prevent duplicate processing
        markValuationCompleted(valuationId);
        
        // Track valuation completion
        trackFormSubmission('valuation_completed', {
          address: address?.replace(/\s+\d+.*$/, ''), // anonymized
          questions_count: questionsAnswers.length,
          event_category: 'Valuation',
          event_label: 'Completed Property Valuation'
        });
        
        // Check if backend already processed ad creation and email sending
        const autoProcessing = response.data.auto_processing || {};
        
        console.log('Auto-processing results from backend:', autoProcessing);
        
        // Conditional logic based on ownership
        if (userOwnsProperty) {
          // User owns property - create ad (step 2) then send email (step 3)
          
          // Check if ad was already created by backend auto-processing
          if (autoProcessing.ad_created || isAdCreated(valuationId)) {
            // Backend already created the ad, skip to completed state
            setCurrentStep(2);
            setStepStates(prev => ({ ...prev, 2: 'completed' }));
            setAdCreationResult({ 
              success: true, 
              message: 'Ad created successfully',
              seller_id: autoProcessing.seller_id,
              seller_data_id: autoProcessing.seller_data_id
            });
            markAdCreated(valuationId);
            
            // Check if email was also sent by backend
            if (autoProcessing.email_sent || isEmailSent(valuationId)) {
              // Email also sent, complete step 3
              setCurrentStep(3);
              setStepStates(prev => ({ ...prev, 3: 'completed' }));
              setEmailResult({ success: true, message: 'Email sent successfully' });
              markEmailSent(valuationId);
            } else {
              // Need to send email manually
              setTimeout(() => {
                setCurrentStep(3);
                setStepStates(prev => ({ ...prev, 3: 'loading' }));
                sendValuationEmail(response.data.valuation_result, autoProcessing.seller_id);
              }, 1500);
            }
          } else {
            // Need to create ad manually
            setTimeout(() => {
              setCurrentStep(2);
              setStepStates(prev => ({ ...prev, 2: 'loading' }));
              createUserAndAd(response.data.valuation_result);
            }, 1500);
          }
        } else {
          // User doesn't own property - skip ad creation, go directly to email (step 2 in non-owner flow)
          
          // Check if email was already sent by backend auto-processing
          if (autoProcessing.email_sent || isEmailSent(valuationId)) {
            // Backend already sent the email, complete step 2
            setCurrentStep(2);
            setStepStates(prev => ({ ...prev, 2: 'completed' }));
            setEmailResult({ success: true, message: 'Email sent successfully' });
            markEmailSent(valuationId);
          } else {
            // Need to send email manually
            setTimeout(() => {
              setCurrentStep(2); // Step 2 is email in non-owner flow
              setStepStates(prev => ({ ...prev, 2: 'loading' }));
              sendEmailDirect(response.data.valuation_result);
            }, 1500);
          }
        }
      } else {
        throw new Error('Valuation generation failed');
      }
    } catch (error) {
      console.error('Error generating valuation:', error);
      
      // Handle specific error cases
      if (error.response?.status === 409) {
        // Valuation already in progress - wait and try to get result
        console.log('Valuation already in progress, checking for completion...');
        setTimeout(() => {
          // Check if valuation completed while we were waiting
          if (isValuationCompleted(valuationId)) {
            setStepStates(prev => ({ ...prev, 1: 'completed' }));
            // Continue with next steps
            if (!isAdCreated(valuationId)) {
              setCurrentStep(2);
              setStepStates(prev => ({ ...prev, 2: 'loading' }));
              createUserAndAd(null);
            }
          } else {
            setStepStates(prev => ({ ...prev, 1: 'error' }));
            setError(t('valuationResult.generationError'));
          }
        }, 2000);
      } else {
        setStepStates(prev => ({ ...prev, 1: 'error' }));
        setError(t('valuationResult.generationError'));
        valuationSubmittedRef.current = false; // Reset on error
        initializationRef.current = false; // Reset on error so user can retry
      }
    } finally {
      // Always reset the generating flag
      setIsGenerating(false);
    }
  };

  // Send valuation email with verification link
  const sendValuationEmail = async (passedValuationResult, sellerId) => {
    // Determine which step we're on (2 for non-owners, 3 for owners)
    const emailStepNumber = userOwnsProperty ? 3 : 2;
    
    try {
      
      // Check if email has already been sent to prevent duplicates
      if (isEmailSent(valuationId)) {
        console.log('Email already sent for valuation ID:', valuationId);
        setStepStates(prev => ({ ...prev, [emailStepNumber]: 'completed' }));
        setEmailResult({ success: true, message: 'Email already sent' });
        return;
      }
      
      // Use passed result or fall back to state
      const resultToUse = passedValuationResult || valuationResult;
      
      console.log('Sending valuation email with result:', resultToUse);
      
      if (!resultToUse) {
        console.warn('No valuation result available for email');
      }
      
      const response = await axios.post(`${backend_url}/api/send-valuation-email`, {
        email: emailFromChat,
        valuation_data: {
          address: address,
          estimated_value: resultToUse?.estimated_value || t('common.notProvided'),
          explanation: resultToUse?.explanation || ''
        },
        seller_id: sellerId,
        language,
        source_domain: allAnswers?.source_domain || getSourceDomain(),
        valuation_market: allAnswers?.valuation_market || getValuationMarket()
      });
      
      if (response.data.success) {
        setEmailResult(response.data);
        setStepStates(prev => ({ ...prev, [emailStepNumber]: 'completed' }));
        
        // Mark email as sent in localStorage to prevent duplicates
        markEmailSent(valuationId);
        
        // Track successful email sending
        trackFormSubmission('valuation_email_sent_success', {
          event_category: 'Valuation',
          event_label: 'Email Sent Successfully from Valuation',
          seller_id: sellerId
        });
      } else {
        throw new Error(response.data.error || 'Email sending failed');
      }
    } catch (error) {
      console.error('Error sending valuation email:', error);
      setStepStates(prev => ({ ...prev, [emailStepNumber]: 'error' }));
      setError(t('valuationResult.emailError'));
    }
  };

  // Create user account and ad from valuation
  const createUserAndAd = async (passedValuationResult) => {
    try {
      // Check if ad has already been created to prevent duplicates
      if (isAdCreated(valuationId)) {
        console.log('Ad already created for valuation ID:', valuationId);
        setStepStates(prev => ({ ...prev, 2: 'completed' }));
        setAdCreationResult({ success: true, message: 'Ad already created' });
        return;
      }
      
      // Use passed result or fall back to state
      const resultToUse = passedValuationResult || valuationResult;
      
      console.log('Creating user and ad with valuation result:', resultToUse);
      
      if (!resultToUse) {
        console.warn('No valuation result available for ad creation');
      }
      
      // Use coordinates and län from allAnswers if available, otherwise geocode
      let latitude = allAnswers?.latitude;
      let longitude = allAnswers?.longitude;
      let lan = allAnswers?.lan;
      
      // If coordinates not available from answers, geocode the address
      if (!latitude || !longitude) {
        const geocodingResult = await geocodeAddress(address);
        latitude = geocodingResult.latitude;
        longitude = geocodingResult.longitude;
        lan = lan || geocodingResult.lan; // Prefer lan from answers if available
      }
      
      const response = await axios.post(`${backend_url}/api/create-user-and-ad-from-valuation`, {
        address: address,
        phone: phoneFromChat,
        email: emailFromChat,
        valuationResult: resultToUse,
        questionsAnswers: questionsAnswers,
        propertyType: allAnswers?.propertyType || '',
        numberOfRooms: allAnswers?.numberOfRooms || '',
        area: allAnswers?.area || '',
        floor: allAnswers?.floor || '',
        monthlyFee: allAnswers?.monthlyFee || '',
        plotSize: allAnswers?.plotSize || '',
        constructionYear: allAnswers?.constructionYear || '',
        condition: allAnswers?.condition || '',
        additionalInfo: allAnswers?.additionalInfo || '',
        latitude: latitude,
        longitude: longitude,
        lan: lan || '',
        ownership: allAnswers?.ownership || '',
        // Pass through tracking IDs if present so backend can create Lead/offline conversion later
        gclid: allAnswers?.gclid || '',
        wbraid: allAnswers?.wbraid || '',
        gbraid: allAnswers?.gbraid || '',
        utm_source: allAnswers?.utm_source || '',
        utm_medium: allAnswers?.utm_medium || '',
        utm_campaign: allAnswers?.utm_campaign || '',
        utm_content: allAnswers?.utm_content || '',
        utm_term: allAnswers?.utm_term || '',
        language,
        // Domain (bodex.se / bodex.es / www.bodex.es) so the backend stores it
        // on the Lead and the offline conversion picks the matching
        // GOOGLE_ADS_CONVERSION_ACTION_ID(_ES).
        source_domain: allAnswers?.source_domain || getSourceDomain(),
        valuation_market: allAnswers?.valuation_market || getValuationMarket()
      });
      
      if (response.data.success) {
        setAdCreationResult(response.data);
        setStepStates(prev => ({ ...prev, 2: 'completed' }));
        
        // Mark ad as created in localStorage to prevent duplicates
        markAdCreated(valuationId);
        
        // Track successful ad creation
        trackFormSubmission('valuation_ad_created_success', {
          event_category: 'Valuation',
          event_label: 'Ad Created Successfully from Valuation (Three-Step)',
          seller_data_id: response.data.seller_data_id
        });
        
        // Only send email if it hasn't been sent yet
        if (!isEmailSent(valuationId)) {
          // Start step 3 after a short delay
          setTimeout(() => {
            setCurrentStep(3);
            setStepStates(prev => ({ ...prev, 3: 'loading' }));
            sendValuationEmail(resultToUse, response.data.seller_id);
          }, 1500);
        } else {
          // Email already sent, skip to completed state
          setCurrentStep(3);
          setStepStates(prev => ({ ...prev, 3: 'completed' }));
          setEmailResult({ success: true, message: 'Email already sent' });
        }
      } else {
        throw new Error(response.data.error || 'Ad creation failed');
      }
    } catch (error) {
      console.error('Error creating user and ad:', error);
      setStepStates(prev => ({ ...prev, 2: 'error' }));
      setError(t('objectForm.validation.unexpected'));
    }
  };

  // Send valuation email directly for non-owners (no user account creation)
  const sendEmailDirect = async (passedValuationResult) => {
    try {
      if (isEmailSent(valuationId)) {
        setStepStates(prev => ({ ...prev, 2: 'completed' }));
        setEmailResult({ success: true, message: 'Email already sent' });
        return;
      }

      // Use passed result or fall back to state
      const resultToUse = passedValuationResult || valuationResult;
      
      console.log('Sending valuation email directly for non-owner with result:', resultToUse);
      
      if (!resultToUse) {
        console.warn('No valuation result available for email');
      }
      
      const response = await axios.post(`${backend_url}/api/send-valuation-email-direct`, {
        address: address,
        phone: phoneFromChat,
        email: emailFromChat,
        valuationResult: resultToUse,
        language,
        source_domain: allAnswers?.source_domain || getSourceDomain(),
        valuation_market: allAnswers?.valuation_market || getValuationMarket()
      });
      
      if (response.data.success) {
        setEmailResult(response.data);
        // For non-owners, step 2 is the email step
        setStepStates(prev => ({ ...prev, 2: 'completed' }));
        markEmailSent(valuationId);
        
        // Track successful email sending for non-owners
        trackFormSubmission('valuation_email_sent_success_non_owner', {
          event_category: 'Valuation',
          event_label: 'Email Sent Successfully from Valuation (Non-Owner)',
          email: emailFromChat
        });
      } else {
        throw new Error(response.data.error || 'Email sending failed');
      }
    } catch (error) {
      console.error('Error sending valuation email for non-owner:', error);
      // For non-owners, step 2 is the email step
      setStepStates(prev => ({ ...prev, 2: 'error' }));
      setError(t('valuationResult.emailError'));
    }
  };

  // Handle login button click
  const handleLoginClick = () => {
    history.push('/login');
  };

  // Cleanup old localStorage entries and start the process when component mounts
  useEffect(() => {
    console.log('ValuationResult useEffect triggered with:', { 
      valuationId, 
      isPending, 
      hasValuationResult: !!valuationResult, 
      isGenerating,
      finalQuestion,
      finalAnswer 
    });
    
    // Clean up old localStorage entries
    cleanupOldEntries();
    
    // Only proceed if we have a valuationId and haven't already processed this
    if (!valuationId || isGenerating || initializationRef.current) {
      console.log('Skipping - no valuationId, already generating, or already initialized');
      return;
    }
    
    // Mark as initialized to prevent multiple executions
    initializationRef.current = true;
    
    // Completed valuations are read-only on refresh; never replay side effects.
    // This also covers older non-owner sessions without an email-sent flag.
    if (isValuationCompleted(valuationId)) {
      setCurrentStep(2);
      setStepStates(prev => ({ ...prev, 2: isEmailSent(valuationId) ? 'completed' : 'pending' }));
      axios.get(backend_url + '/api/get-valuation-result/' + valuationId)
        .then(response => {
          if (!response.data.success || !response.data.valuation_result) {
            throw new Error('Valuation result unavailable');
          }
          setValuationResult(response.data.valuation_result);
          setStepStates(prev => ({ ...prev, 1: 'completed' }));
        })
        .catch(() => {
          setStepStates(prev => ({ ...prev, 1: 'error' }));
          setError(t('valuationResult.noValuation'));
        });
      return;
    }

    if (isPending && !valuationResult) {
      console.log('Starting valuation generation for new valuation ID:', valuationId);
      setIsGenerating(true);
      generateValuation();
    } else if (!isPending && !valuationResult) {
      // Valuation already submitted, try to retrieve result
      console.log('Valuation already submitted, retrieving result for ID:', valuationId);
      setIsGenerating(true);
      generateValuation();
    }
  }, [valuationId]); // Trigger only when valuationId changes - refs will prevent duplicate executions

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  }, []);

  const handleGoBack = () => {
    history.goBack();
  };

  const handleGoHome = () => {
    history.push('/');
  };

  // Render step icon based on state
  const renderStepIcon = (stepId) => {
    const state = stepStates[stepId];
    switch (state) {
      case 'loading':
        return <FontAwesomeIcon icon={faSpinner} className="fa-spin" />;
      case 'completed':
        return <FontAwesomeIcon icon={faCheck} />;
      case 'error':
        return <FontAwesomeIcon icon={faCheck} className="error-icon" />;
      default:
        return <span className="vr-step-number">{stepId}</span>;
    }
  };

  // If no valuationId provided or there's an unrecoverable error, show error
  if (!valuationId || (error && error.includes('hittades'))) {
    return (
      <div className="valuation-result-container">
        <div className="valuation-header">
          <button onClick={handleGoHome} className="vr-back-button">
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>{t('common.back')}</span>
          </button>
          <h1>
            <FontAwesomeIcon icon={faCalculator} />
            {t('valuationResult.valuationTitle')}
          </h1>
        </div>
        <div className="vr-error-message">
          <p>{t('valuationResult.noValuation')}</p>
          <button onClick={handleGoHome} className="vr-home-button">
            <FontAwesomeIcon icon={faHome} />
            {t('valuationResult.backHome')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="valuation-result-container">
      <div className="valuation-header">
        <button onClick={handleGoBack} className="vr-back-button">
          <FontAwesomeIcon icon={faArrowLeft} />
          <span>{t('common.back')}</span>
        </button>
        <h1>
          <FontAwesomeIcon icon={faCalculator} />
          {t('valuationResult.title')}
        </h1>
      </div>

      {/* Property info section */}
      <div className="vr-property-info">
        <p>
          <FontAwesomeIcon icon={faHome} />
          {address}
        </p>
      </div>

          {/* Valuation result section - show when step 1 is completed */}
        {valuationResult && stepStates[1] === 'completed' && (
        <div className="valuation-result-content">
          <div className="valuation-card">
            <div className="valuation-content">
              <h3>{t('valuationResult.done')}</h3>
              <div className="valuation-price">
                <span className="price-label">{t('valuationResult.estimatedValue')}</span>
                <span className="price-amount">{valuationResult.estimated_value}</span>
              </div>
              <div className="valuation-explanation">
                <p>{valuationResult.explanation}</p>
              </div>
              <div className="valuation-disclaimer">
                <p><small>
                  {t('valuationResult.disclaimer')}
                </small></p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* The offer follows the result independently of background email/ad processing. */}
      {valuationResult && stepStates[1] === 'completed' && (
        <BodexPlusOffer address={address} email={emailFromChat || allAnswers?.email || ''} />
      )}

      {/* Progress tracker */}
      <div className="vr-progress-tracker">
        <h3>{t('valuationResult.processing')}</h3>
        <div className="vr-progress-steps">
          {steps.map((step) => (
            <div 
              key={step.id} 
              className={`vr-progress-step ${
                currentStep === step.id ? 'active' : ''
              } ${stepStates[step.id]}`}
            >
              <div className="vr-step-icon">
                {renderStepIcon(step.id)}
              </div>
              <div className="vr-step-content">
                <div className="vr-step-title">{step.title}</div>
                <div className="vr-step-description">{step.description}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="vr-error-section">
          <p>{error}</p>
          <button onClick={handleGoHome} className="vr-home-button">
            <FontAwesomeIcon icon={faHome} />
            {t('valuationResult.backHome')}
          </button>
        </div>
      )}

      {/* Action buttons */}
      <div className="vr-action-buttons">
        <button onClick={handleGoHome} className="vr-home-button">
          <FontAwesomeIcon icon={faHome} />
          {t('valuationResult.backHome')}
        </button>
      </div>
    </div>
  );
};

export default ValuationResult; 
